import { format } from 'date-fns';
import {
  Dispatch,
  Fragment,
  ReactElement,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';
import {
  getIconButtonStyles,
  IconButton,
  Pill,
  Button,
  LinearLoader,
} from '@sc/shared-ui';
import Collapse from '../assets/icons/collapse.svg';
import {
  Menu,
  MenuButton,
  MenuItems,
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react';
import {
  ArrowLeftIcon,
  ArrowLeftStartOnRectangleIcon,
  BellIcon,
  CheckIcon,
  CircleStackIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleIconSolid,
  ExclamationCircleIcon as ExclamationCircleIconSolid,
} from '@heroicons/react/24/solid';
import { useUser } from '../providers/users';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  GQL_QUERY_ME,
  GQL_MUTATION_UPDATE_NOTIFICATION,
  GQL_QUERY_INDEX_NOTIFICATIONS,
} from '../utility/queries';
import { Link, useCanGoBack, useRouter } from '@tanstack/react-router';
import { useAlerts } from '../providers/alerts';
import { useBreadCrumbs } from '../providers/breadcrumbs';
import DataFlow from '../assets/icons/dataflow-04.svg';
import { Tooltip } from 'react-tooltip';
import { useUsage, UseUsageType } from '../hooks/useUsage';
import { UsageBars } from '../components/UsageBars';
import { track } from '../utility/analytics';

interface Props {
  collapsed: boolean;
  setCollapsed: Dispatch<SetStateAction<boolean>>;
}

// The panel unmounts when closed, so mounting means opened — the toggle's
// onClick would also fire when closing.
const TrackNotificationsOpened = ({
  unreadCount,
}: {
  unreadCount: number;
}): null => {
  const unreadOnOpen = useRef(unreadCount);

  useEffect(() => {
    track('Notifications Panel Opened', { unreadCount: unreadOnOpen.current });
  }, []);

  return null;
};

const Usage = ({
  usage: { usage, usageState, refresh, fetching },
  open,
}: {
  open: boolean;
  usage: UseUsageType;
}) => {
  useEffect(() => {
    if (open) {
      track('Usage Panel Opened');

      void refresh();
    }
  }, [open, refresh]);

  return (
    <>
      {fetching && <LinearLoader />}
      <div className="flex overflow-hidden w-full">
        <div className="flex flex-col space-y-5 px-5 w-full my-5">
          <UsageBars usage={usage} usageState={usageState} />
          {usageState === 'warning' && (
            <div className="p-1 border border-red-700 rounded-xs mt-5">
              <div className="p-3 flex items-start space-x-3">
                <ExclamationCircleIconSolid className="size-5 text-red-400 shrink-0" />
                <span className="text-sm">
                  Running out of usage will immediately disable all of your
                  knowledge bases. Visit{' '}
                  <Link
                    className="underline font-bold"
                    to="/billing"
                    onClick={() => {
                      track('Usage Panel Billing Clicked');

                      close();
                    }}
                  >
                    billing
                  </Link>{' '}
                  to upgrade your plan.
                </span>
              </div>
            </div>
          )}
          {usageState === 'notice' && (
            <div className="p-1 border border-grey-400 rounded-xs mt-5">
              <div className="p-3 flex items-start space-x-3">
                <ExclamationCircleIconSolid className="size-5 text-grey-400 shrink-0" />
                <span className="text-sm">
                  Running out of usage will immediately disable all of your
                  knowledge bases. Visit{' '}
                  <Link
                    onClick={() => {
                      track('Usage Panel Billing Clicked');

                      close();
                    }}
                    className="underline font-bold"
                    to="/billing"
                  >
                    billing
                  </Link>{' '}
                  to upgrade your plan.
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export const Topbar = ({ collapsed, setCollapsed }: Props): ReactElement => {
  const { user } = useUser();

  const router = useRouter();

  const { crumbs } = useBreadCrumbs();

  const canGoBack = useCanGoBack();

  const { data } = useQuery(GQL_QUERY_INDEX_NOTIFICATIONS, {
    pollInterval: 5000,
    skipPollAttempt: () => !document.hasFocus(),
  });

  const [updateNotification] = useMutation(GQL_MUTATION_UPDATE_NOTIFICATION);

  const unread =
    data?.indexNotifications.filter((n) => !n.isViewed).length ?? 0;

  const { setAlertModal } = useAlerts();

  const usage = useUsage();

  return (
    <div className="px-5 py-3 bg-background-primary w-full border-b border-grey-t-800 flex items-center justify-between">
      <div className="flex items-center space-x-2">
        <IconButton
          onClick={() => setCollapsed((c) => !c)}
          Icon={Collapse}
          iconClassName={collapsed ? 'rotate-180' : ''}
          tooltip={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        />
        {crumbs.length > 0 && (
          <>
            {canGoBack && (
              <IconButton
                Icon={ArrowLeftIcon}
                onClick={() => router.history.back()}
                tooltip="Go back"
              />
            )}
            <div className="space-x-2">
              {crumbs.map(({ label, ...link }, i) =>
                i === crumbs.length - 1 ? (
                  <span
                    key={label}
                    className="text-sm font-semibold text-secondary-400"
                  >
                    {label}
                  </span>
                ) : (
                  <Fragment key={label}>
                    <Link
                      className="text-sm underline font-semibold hover:no-underline"
                      {...link}
                    >
                      {label}
                    </Link>
                    <span className="text-grey-600">/</span>
                  </Fragment>
                )
              )}
            </div>
          </>
        )}
      </div>
      <ul className="flex space-x-5">
        <li>
          <Popover className="relative">
            {({ open }) => (
              <>
                <PopoverButton
                  {...getIconButtonStyles({
                    type: 'basic',
                    size: 'small',
                    disabled: false,
                  })}
                  data-tooltip-content="Token usage"
                  data-tooltip-place="bottom"
                  data-tooltip-id="credits"
                  data-tooltip-delay-show={350}
                >
                  <CircleStackIcon className="size-6" />
                  <div className="absolute px-2 -top-0 -left-[8px]">
                    {usage.usageState === 'healthy' && (
                      <CheckCircleIconSolid className="size-5 text-primary-400" />
                    )}
                    {usage.usageState === 'notice' && (
                      <ExclamationCircleIconSolid className="size-5 text-yellow-400" />
                    )}
                    {usage.usageState === 'warning' && (
                      <ExclamationCircleIconSolid className="size-5 text-red-400" />
                    )}
                  </div>
                  <Tooltip
                    openEvents={{ mouseover: true }}
                    closeEvents={{ click: true, mouseleave: true, blur: true }}
                    className="z-20"
                    id="credits"
                  />
                </PopoverButton>
                <PopoverPanel
                  anchor={{
                    to: 'bottom',
                    padding: 12,
                    gap: 8,
                  }}
                  transition
                  className="z-50 text-white flex origin-top flex-col transition data-closed:-translate-y-full bg-background-tertiary rounded shadow-xl data-closed:opacity-0 w-full max-w-105! border border-grey-t-800"
                >
                  {({ close, open }) => (
                    <div>
                      <div className="flex justify-between items-center p-4 border-b border-b-grey-700">
                        <div className="flex items-center gap-x-3">
                          <h2 className="font-bold font-grotesque text-h2">
                            Usage
                          </h2>
                          <Link
                            to="/billing"
                            onClick={() => {
                              track('Usage Panel Billing Clicked');

                              close();
                            }}
                          >
                            <Pill
                              colour={
                                user.company.subscription
                                  ? '#7885f9'
                                  : '#d271e2'
                              }
                              outline
                              size="small"
                              text={
                                user.company.subscription?.type
                                  ? user.company.subscription.type
                                      .charAt(0)
                                      .toUpperCase() +
                                    user.company.subscription.type.slice(1)
                                  : 'Free Trial'
                              }
                            />
                          </Link>
                        </div>
                        <IconButton
                          onClick={() => close()}
                          size="large"
                          Icon={XMarkIcon}
                          tooltip="Close"
                        />
                      </div>
                      <Usage open={open} usage={usage} />
                    </div>
                  )}
                </PopoverPanel>
              </>
            )}
          </Popover>
        </li>
        <li>
          <Popover className="relative">
            <PopoverButton
              {...getIconButtonStyles({
                type: 'basic',
                size: 'small',
                disabled: false,
              })}
              data-tooltip-content="Notifications"
              data-tooltip-place="bottom"
              data-tooltip-id="notifications"
              data-tooltip-delay-show={350}
            >
              <BellIcon className="size-6" />
              {!!unread && (
                <div className="bg-red-400 size-4.5 absolute text-white top-0 -left-[3px] rounded-full overflow-hidden flex items-center justify-center" />
              )}
              <Tooltip
                openEvents={{ mouseover: true }}
                closeEvents={{ click: true, mouseleave: true, blur: true }}
                className="z-20"
                id="notifications"
              />
            </PopoverButton>
            <PopoverPanel
              anchor={{
                to: 'bottom',
                padding: 12,
                gap: 8,
              }}
              transition
              className="z-50 text-white flex origin-top flex-col transition data-closed:-translate-y-full bg-background-tertiary rounded shadow-xl data-closed:opacity-0 w-full max-w-105! border border-grey-t-800"
            >
              {({ close }) => (
                <div>
                  <TrackNotificationsOpened unreadCount={unread} />
                  <div className="flex justify-between items-center p-4 border-b border-b-grey-700">
                    <h2 className="font-bold font-grotesque text-h2">
                      Notifications
                    </h2>
                    <IconButton
                      onClick={() => close()}
                      size="large"
                      Icon={XMarkIcon}
                      tooltip="Close"
                    />
                  </div>
                  <div className="flex overflow-hidden w-full">
                    <div className="overflow-auto w-full">
                      {data?.indexNotifications.length ? (
                        data.indexNotifications.map((n) => (
                          <div
                            key={n.uuid}
                            className="p-4 w-full last:border-b-none border-b border-b-grey-700"
                          >
                            <div className="flex items-center justify-between">
                              <h4 className="text-lg font-bold">{n.title}</h4>
                              {!n.isViewed && (
                                <Pill
                                  size="small"
                                  colour="#E74A4A"
                                  text="New"
                                />
                              )}
                            </div>
                            <p className="mt-2 mb-3">{n.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-text-low text-xs">
                                {format(n.createdAt, 'do MMMM yyyy • HH:mm')}
                              </span>
                              <div className="flex space-x-2">
                                <Button
                                  button={{
                                    onClick: () =>
                                      void updateNotification({
                                        variables: {
                                          input: {
                                            uuid: n.uuid,
                                            isDismissed: true,
                                            isViewed: undefined,
                                          },
                                        },
                                        optimisticResponse: {
                                          updateNotification: {
                                            ...n,
                                            isDismissed: true,
                                          },
                                        },
                                        refetchQueries: [GQL_QUERY_ME],
                                        onCompleted: () =>
                                          track('Notification Dismissed'),
                                        update: (cache) => {
                                          cache.updateQuery(
                                            {
                                              query:
                                                GQL_QUERY_INDEX_NOTIFICATIONS,
                                            },
                                            (d) =>
                                              d
                                                ? {
                                                    ...d,
                                                    indexNotifications:
                                                      d.indexNotifications.filter(
                                                        (notif) =>
                                                          notif.uuid !== n.uuid
                                                      ),
                                                  }
                                                : null
                                          );
                                        },
                                      }),
                                    text: 'Dismiss',
                                  }}
                                />
                                <Button
                                  button={{
                                    text: 'View',
                                    onClick: () => {
                                      void updateNotification({
                                        variables: {
                                          input: {
                                            uuid: n.uuid,
                                            isViewed: true,
                                            isDismissed: undefined,
                                          },
                                        },
                                        optimisticResponse: {
                                          updateNotification: {
                                            ...n,
                                            isViewed: true,
                                          },
                                        },
                                        refetchQueries: [GQL_QUERY_ME],
                                        onCompleted: () =>
                                          track('Notification Viewed', {
                                            path: n.path,
                                          }),
                                        update: (cache, data) => {
                                          cache.updateQuery(
                                            {
                                              query:
                                                GQL_QUERY_INDEX_NOTIFICATIONS,
                                            },
                                            (d) =>
                                              d
                                                ? {
                                                    ...d,
                                                    indexNotifications:
                                                      d.indexNotifications.map(
                                                        (notif) => {
                                                          if (
                                                            notif.uuid ===
                                                            n.uuid
                                                          )
                                                            return (
                                                              data.data
                                                                ?.updateNotification ??
                                                              notif
                                                            );
                                                          return notif;
                                                        }
                                                      ),
                                                  }
                                                : null
                                          );
                                        },
                                      });

                                      void router.navigate({
                                        to: `/${n.path}`,
                                      });

                                      close();
                                    },
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="flex flex-col items-center mb-8 text-center p-6">
                          <div className="rounded bg-primary-400/10 p-5 mb-5 ">
                            <CheckIcon className="text-primary-400 size-11" />
                          </div>
                          <h1 className="text-2xl font-semibold font-poppins mb-2">
                            Nothing left to see
                          </h1>
                          <p className="text-text-low">
                            You&apos;ve been productive!
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </PopoverPanel>
          </Popover>
        </li>
        <li className="flex items-center">
          <Menu>
            <MenuButton
              {...getIconButtonStyles({
                type: 'basic',
                size: 'small',
                disabled: false,
              })}
              onClick={() => track('Account Menu Opened')}
              data-tooltip-content="Account menu"
              data-tooltip-place="bottom"
              data-tooltip-id="account-menu"
              data-tooltip-delay-show={350}
            >
              <UserCircleIcon className="size-6" />
              <Tooltip
                openEvents={{ mouseover: true }}
                closeEvents={{ click: true, mouseleave: true, blur: true }}
                className="z-20"
                id="account-menu"
              />
            </MenuButton>
            <MenuItems
              portal
              anchor={{
                to: 'bottom start',
                padding: 12,
              }}
              className="w-40 z-60 outline-hidden border border-grey-t-800 shadow-xl rounded bg-background-tertiary text-white flex flex-col empty:hidden min-w-60"
            >
              <div className="flex flex-col items-center justify-center border-b border-grey-700 py-4 space-y-1">
                {user.name && <h4 className="font-semibold">{user.name}</h4>}
                <p className="font-semibold text-text-low-priority text-sm">
                  {user.email}
                </p>
                <p className="font-semibold text-text-low-priority text-sm">
                  {user.role.slice(0, 1).toUpperCase()}
                  {user.role.slice(1)}
                  {user.company.name ? ` @ ${user.company.name}` : ''}
                </p>
              </div>
              <ul className="p-2 space-y-2">
                <li>
                  <Link
                    to="/integrations"
                    onClick={() =>
                      track('Account Menu Item Clicked', {
                        item: 'integrations',
                      })
                    }
                    className="data-[status=active]:bg-grey-t-800 flex items-center px-2 py-2.5 rounded-xs hover:bg-grey-t-900 active:shadow-focused-dark space-x-2 group"
                  >
                    <DataFlow className="size-5 group-data-[status=active]:text-primary-400 text-grey-t-400 shrink-0" />
                    <span className="group-data-[collapsed=true]:opacity-0 transition-all text-body-medium font-semibold">
                      Integrations
                    </span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/settings"
                    onClick={() =>
                      track('Account Menu Item Clicked', { item: 'settings' })
                    }
                    className="data-[status=active]:bg-grey-t-800 flex items-center px-2 py-2.5 rounded-xs hover:bg-grey-t-900 active:shadow-focused-dark space-x-2 group"
                  >
                    <Cog6ToothIcon className="size-5 group-data-[status=active]:text-primary-400 text-grey-t-400 shrink-0" />
                    <span className="group-data-[collapsed=true]:opacity-0 transition-all text-body-medium font-semibold">
                      Settings
                    </span>
                  </Link>
                </li>

                <li>
                  <button
                    onClick={() => {
                      track('Account Menu Item Clicked', { item: 'help' });

                      setAlertModal({
                        open: true,
                        closeText: 'Close',
                        title: 'Need help?',
                        description: (
                          <p>
                            Contact{' '}
                            <a
                              className="text-primary-400 underline"
                              href="mailto:support@starkchat.com"
                            >
                              support@starkchat.com
                            </a>{' '}
                            and we will get back to you as soon as possible.
                          </p>
                        ),
                        icon: 'info',
                      });
                    }}
                    className="data-[status=active]:bg-grey-t-800 flex items-center px-2 py-2.5 rounded-xs hover:bg-grey-t-900 active:shadow-focused-dark space-x-2 group w-full"
                  >
                    <QuestionMarkCircleIcon className="size-5 group-data-[status=active]:text-primary-400 text-grey-t-400 shrink-0" />
                    <span className="group-data-[collapsed=true]:opacity-0 transition-all text-body-medium font-semibold">
                      Help
                    </span>
                  </button>
                </li>
                <li>
                  <Link
                    to="/logout"
                    onClick={() =>
                      track('Account Menu Item Clicked', { item: 'logout' })
                    }
                    className="data-[status=active]:bg-grey-t-800 flex items-center px-2 py-2.5 rounded-xs hover:bg-grey-t-900 active:shadow-focused-dark space-x-2 group"
                  >
                    <ArrowLeftStartOnRectangleIcon className="size-5 group-data-[status=active]:text-primary-400 text-grey-t-400 shrink-0" />
                    <span className="group-data-[collapsed=true]:opacity-0 transition-all text-body-medium font-semibold">
                      Logout
                    </span>
                  </Link>
                </li>
              </ul>
            </MenuItems>
          </Menu>
        </li>
      </ul>
    </div>
  );
};
